import { HttpStatusCode, isAxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { logger } from '@dist-marketplace-frontend/services';

import type { FormPayloadSchema } from '../schema/carFinance.schema';
import { carFinanceApi } from '../../../../api/carFinanceApi';
import { PAGE } from '../../../../helpers/pages';

const validationErrorSchema = z.array(
  z.object({
    message: z.string(),
    field: z.string(),
  }),
);

type ValidationError = z.infer<typeof validationErrorSchema>;

type UseSubmitFinanceFormProps = {
  onValidationError: (error: ValidationError) => void;
  onSuccess: () => void;
};

const submitFinanceForm = async (payload: FormPayloadSchema) => {
  const { data } = await carFinanceApi.submit(payload);
  return data;
};

export const useSubmitFinanceForm = (props: UseSubmitFinanceFormProps) => {
  const { onValidationError, onSuccess } = props;
  return useMutation({
    mutationKey: ['submitFinanceForm'],
    mutationFn: (options: FormPayloadSchema) => submitFinanceForm(options),
    onError: (error) => {
      if (
        !isAxiosError(error) ||
        error.response?.status !== HttpStatusCode.UnprocessableEntity
      ) {
        throw error;
      }
      const { data } = error.response;
      const parsedError = validationErrorSchema.safeParse(data);
      if (parsedError.success) {
        onValidationError(parsedError.data);
      } else {
        logger.error({
          error,
          message: 'Car finance form validation error field mismatch',
          tags: [PAGE.CAR_FINANCE],
        });
      }
    },
    onSuccess,
  });
};
